import { lazy, Suspense, useCallback, useEffect, useRef, useState } from 'react';
import { api, ApiError, LiveSocket, storeToken, storedToken } from './api/client.js';
import type { AccountView, CharacterView, Settlement } from './api/types.js';
import { useLocale } from './i18n/Locale.js';
import { LogoutShortcut } from './components/LogoutConfirm.js';
import { AuthScreen, isPublicAuthPath } from './screens/AuthScreen.js';
import { KazbiaLogo } from './components/KazbiaLogo.js';
// The game (and its data package, ~12 MB of module) and the character list load on their own: the
// login / about pages show without them (owner 24/10: the about page's outfits in ~1.2 s like the
// reference). They are fetched in the background as soon as the entry page is up (preloadGame).
const loadGame = () => import('./screens/GameScreen.js');
const loadSelect = () => import('./screens/SelectScreen.js');
const GameScreen = lazy(() => loadGame().then((m) => ({ default: m.GameScreen })));
const SelectScreen = lazy(() => loadSelect().then((m) => ({ default: m.SelectScreen })));
let preloadStarted = false;
/** Start downloading the character list and the game (once). */
function preloadGame(): void {
  if (preloadStarted) return;
  preloadStarted = true;
  void loadSelect().catch(() => { preloadStarted = false; });
  void loadGame().catch(() => { preloadStarted = false; });
}

type Screen = 'boot' | 'auth' | 'select' | 'game';

const LAST_CHARACTER_KEY = 'tibia-idle.character';

function rememberedCharacter(): number | null {
  try {
    const raw = localStorage.getItem(LAST_CHARACTER_KEY);
    const id = raw ? Number(raw) : NaN;
    return Number.isInteger(id) && id > 0 ? id : null;
  } catch {
    return null;
  }
}

function rememberCharacter(id: number | null): void {
  try {
    if (id === null) localStorage.removeItem(LAST_CHARACTER_KEY);
    else localStorage.setItem(LAST_CHARACTER_KEY, String(id));
  } catch {
    // Storage blocked: the player just picks the character again.
  }
}

/** Splash while a screen's code is still coming (same look as the boot screen). */
function Splash({ text }: { text: string }) {
  // The logo downloads only if the splash stays up a moment: signed out it shows for one frame
  // before the login / about page, and its picture was a second logo download there.
  const [logoAfter] = useState(() => new Promise<void>((resolve) => { window.setTimeout(resolve, 250); }));
  return (
    <div className="auth-page">
      <div className="auth-page-bg" aria-hidden />
      <div className="auth-page-vignette" aria-hidden />
      <div className="splash auth-card-enter">
        <h1><KazbiaLogo variant="splash" after={logoAfter} /></h1>
        <p>{text}</p>
      </div>
    </div>
  );
}

export function App() {
  const { t } = useLocale();
  const [screen, setScreen] = useState<Screen>('boot');
  const [characters, setCharacters] = useState<CharacterView[]>([]);
  const [account, setAccount] = useState<AccountView | null>(null);
  const [character, setCharacter] = useState<CharacterView | null>(null);
  const [settlement, setSettlement] = useState<Settlement | null>(null);
  const [socketStatus, setSocketStatus] = useState<'connecting' | 'open' | 'closed'>('closed');
  const [hadSocket, setHadSocket] = useState(false);

  // Stable identity: GameScreen timers (online training, …) list it as a
  // dependency, and a new function on every socket push restarted them
  // before they could ever fire.
  const latest = useRef<{ id: number; asOf: number }>({ id: 0, asOf: 0 });
  const applyCharacter = useCallback((next: CharacterView, nextSettlement?: Settlement | null) => {
    // Action replies and socket pushes race: a push taken before an action
    // can land after its reply. Keep the newest snapshot of a character.
    const asOf = next.asOf ?? 0;
    if (next.id === latest.current.id && asOf > 0 && asOf < latest.current.asOf) return;
    latest.current = { id: next.id, asOf: Math.max(asOf, next.id === latest.current.id ? latest.current.asOf : 0) };
    setCharacter(next);
    if (nextSettlement !== undefined) setSettlement(nextSettlement);
  }, []);

  async function loadRoster() {
    const payload = await api.characters();
    setCharacters(payload.characters);
    setAccount(payload.account ?? null);
    return payload.characters;
  }

  function enterGame(id: number): Promise<void> {
    return api.character(id).then((payload) => {
      latest.current = { id: 0, asOf: 0 };
      applyCharacter(payload.character, payload.settlement);
      setScreen('game');
      rememberCharacter(id);
    });
  }

  function logout() {
    void api.logout().catch(() => undefined);
    storeToken(null);
    rememberCharacter(null);
    setCharacter(null);
    try {
      window.history.replaceState(null, '', '/login');
    } catch {
      /* sandboxed: the auth screen opens on login anyway */
    }
    setScreen('auth');
  }

  useEffect(() => {
    // The about page and a reset link open as such even with a saved session.
    if (!storedToken() || isPublicAuthPath(window.location.pathname)) {
      setScreen('auth');
      // The entry page first; then the game downloads in the background (cache-warm by the
      // time the player signs in). After the page's own first images, or 1.5 s at the latest.
      const start = () => preloadGame();
      const ric = (window as Window & { requestIdleCallback?: (cb: () => void, o?: { timeout: number }) => number }).requestIdleCallback;
      const timer = window.setTimeout(() => (ric ? ric(start, { timeout: 1500 }) : start()), 600);
      return () => window.clearTimeout(timer);
    }
    // A saved session goes straight to the characters / the game: fetch them right away.
    preloadGame();
    void loadRoster()
      .then((roster) => {
        // A reload (or coming back to the tab later) drops the player straight
        // back into the character they were playing, like the reference game.
        const last = rememberedCharacter();
        if (last !== null && roster.some((entry) => entry.id === last)) {
          return enterGame(last).catch(() => setScreen('select'));
        }
        setScreen('select');
        return undefined;
      })
      .catch((error: unknown) => {
        // Only a rejected token logs the player out; a server that is
        // restarting (or a flaky network) must not throw the session away.
        if (error instanceof ApiError && (error.status === 401 || error.status === 403)) storeToken(null);
        setScreen('auth');
      });
  }, []);

  useEffect(() => {
    if (screen !== 'game' || !character) return;
    const socket = new LiveSocket(
      character.id,
      (next, nextSettlement) => applyCharacter(next, nextSettlement),
      (status) => {
        setSocketStatus(status);
        if (status === 'open') setHadSocket(true);
      },
    );
    return () => {
      socket.close();
      setSocketStatus('closed');
      setHadSocket(false);
    };
  }, [screen, character?.id]);

  if (screen === 'boot') return <Splash text={t('boot')} />;

  if (screen === 'auth') {
    return (
      <AuthScreen
        hasSession={Boolean(storedToken())}
        onReady={() => {
          void loadRoster()
            .then(() => setScreen('select'))
            .catch((error: unknown) => {
              // A stale token from the about page: back to signing in.
              if (error instanceof ApiError && (error.status === 401 || error.status === 403)) {
                storeToken(null);
                window.location.assign('/login');
              }
            });
        }}
      />
    );
  }

  if (screen === 'select') {
    return (
      <Suspense fallback={<Splash text={t('boot')} />}>
      <SelectScreen
        characters={characters}
        account={account}
        onEnter={(id) => {
          void enterGame(id).catch(() => undefined);
        }}
        onRefresh={async () => {
          await loadRoster();
        }}
        onLogout={logout}
      />
      </Suspense>
    );
  }

  if (!character) return null;

  return (
    <Suspense fallback={<Splash text={t('boot')} />}>
      {hadSocket && socketStatus !== 'open' && (
        <div className="lost-link">{t('connectionLost')}</div>
      )}
      {/* Ctrl+Q (owner 16/10): back to the character list, the session stays signed in. */}
      <LogoutShortcut
        onLogout={() => {
          rememberCharacter(null);
          void loadRoster().catch(() => undefined).then(() => setScreen('select'));
        }}
      />
      <GameScreen
        character={character}
        settlement={settlement}
        onBack={(endHunt = false) => {
          rememberCharacter(null);
          void (async () => {
            // Leaving to the character list keeps the hunt running (it goes on
            // offline) unless the player asked to end it.
            if (endHunt && (character.session || character.queue)) {
              try {
                await api.stopHunt(character.id);
              } catch {
                // Hunt may have ended already.
              }
            }
            await loadRoster();
            setScreen('select');
          })();
        }}
        onCharacter={applyCharacter}
      />
    </Suspense>
  );
}
